'use client';

import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';

import {
    RadioField,
    SelectField,
    StockStatusEnum,
    TextPassage,
} from '@petsmart-ui/sparky';

import {
    TrackingParams,
    trackAddToCart,
    trackAddToCartMethod,
    trackEEAddToCart,
    trackEEProductDetailView,
    trackProductView,
    useAnalyticsContext,
    velocityTrackAddToCart,
} from '@libs/analytics';
import { type Currency, DeliveryType } from '@libs/core';
import { AddToCartType } from '@libs/ocapi';
import type { Store } from '@libs/common-node';
import {
    AUTOSHIP_CONST,
    getFrequencyOptions,
    validateStockType,
} from '@libs/shared-ui';
import type { StockStatusType } from '@libs/shared-ui';
import type { ProductItem } from '@libs/common-next';
import {
    getCartCount,
    NavigationListTypes,
    useOptimizely,
    usePageLayout,
} from '@libs/common-next';

import InStoreOnly from '@app/pdp/components/AddToCart/DeliveryMethods/InStoreOnly';
import AddedToCartDrawer from '@app/pdp/components/AddedToCartDrawer';
import { usePersonalizedProduct } from '@app/pdp/components/PersonalizedProduct/hooks';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { hasCaseRequiredAndQuantity, mapAddToCartItems } from '@app/pdp/utils';

import { useInventories } from '@app/pdp/entities/inventories';
import { getDefaultSelectedInventory } from '../utils';
import type { DeliveryMethodsProps, HandleAddToCart } from '../types';
import './DeliveryMethodsOpti.scss';
import AutoshipHeader from '@app/pdp/components/AutoshipHeader/AutoshipHeader';
import BopisTile from './Tile/BopisTile';
import AutoshipTile from './Tile/AutoshipTile';
import SameDayDeliveryTile from './Tile/SameDayDeliveryTile';
import ShipToMeTile from './Tile/ShipToMeTile';
import AutoShipInfoDrawer from './AutoShipInfoDrawer';

export default function DeliveryMethodsOpti({
    product,
    autoship,
    curbsideInfo,
    handleAddToExistingSubscription,
    vetRxCats,
    defaultStore,
}: Readonly<DeliveryMethodsProps>) {
    const [currentStore, setCurrentStore] = useState<Store | null | undefined>(
        defaultStore
    );
    const [isClient, setIsClient] = useState(false);
    const { getSearchStats } = useAnalyticsContext();
    const {
        selectedDeliveryMethod,
        setSelectedDeliveryMethod,
        selectedProduct,
        handleAddToCart,
        isBopisDisabled,
        isSddDisabled,
        setIsInStoreOnly,
        isAutoshipAvailable,
        isShipToHomeSupported,
        isAutoshipSupported,
        isAllMethodsDisabled,
        selectedVariants,
    } = useProductDetails();

    const {
        ocapi,
        setCurrentVariantId,
        setCurrentBopisMinimumCaseQty,
        selectedStore,
        orderGrooveData,
    } = usePageLayout();
    const oneTime = 'one-time';
    const autoshipOpti = 'autoship-opti';
    const [basketItems, setBasketItems] = useState<ProductItem[]>([]);
    const [radioSelection, setRadioSelection] = useState('one-time');

    const [oneTimeRadioSelection, setOneTimeRadioSelection] =
        useState<DeliveryType>(DeliveryType.BOPIS);
    const [autoshipRadioSelection, setAutoshipRadioSelection] =
        useState<DeliveryType>(DeliveryType.AUTOSHIP);
    const {
        isLoading: inventoryLoading,
        selectedProductInventories: {
            bopis: bopisInventory,
            sdd: sddInventory,
            web: webInventory,
        },
    } = useInventories();

    const { isLoading: isCartCTLoading } = useAddToCTCart();

    const { customPharmacyOOS } = selectedProduct;
    const bopisMinimumCaseQty = selectedProduct.bopisCaseQty ?? 0;
    const minimumCaseQty = hasCaseRequiredAndQuantity(selectedProduct)
        ? (selectedProduct?.customCaseQuantity as number)
        : 0;
    const isPageLoading =
        inventoryLoading || ocapi.isLoading || isCartCTLoading;

    const [deliveryMinimumCaseQty, setDeliveryMinimumCaseQty] =
        useState<number>(minimumCaseQty);
    const [bopisStockStatus, setBopisStockStatus] = useState<StockStatusType>(
        validateStockType(
            {
                stockQuantity: bopisInventory.ats,
                minimumCaseQty: bopisMinimumCaseQty,
                customPharmacyOOS,
            },
            inventoryLoading
        )
    );
    const [sddStockStatus, setSddStockStatus] = useState<StockStatusType>(
        validateStockType(
            {
                stockQuantity: sddInventory.ats,
                minimumCaseQty,
                customPharmacyOOS,
            },
            inventoryLoading
        )
    );
    const [deliveryStockStatus, setDeliveryStockStatus] =
        useState<StockStatusType>(
            validateStockType(
                {
                    stockQuantity: webInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

    const { isPersonalized } = usePersonalizedProduct();

    const handleAddToCartWrapper: HandleAddToCart = async (
        productData,
        basketId
    ) => {
        const response = await handleAddToCart(productData, basketId);

        if (!response || 'fault' in response) {
            return response;
        }

        const productIds = productData.map(
            productItem => productItem.product_id
        );

        const products = response?.product_items?.filter(productItem => {
            if (productIds.includes(productItem.product_id)) {
                productIds.splice(
                    productIds.indexOf(productItem.product_id),
                    1
                );
                return true;
            }
            return false;
        });

        setBasketItems(products || []);

        const sfccProduct = productData.find(
            data => data.product_id === selectedProduct.objectID
        );

        if (sfccProduct) {
            trackEEAddToCart(
                [
                    {
                        algoliaHit: product,
                        parameters: {
                            quantity: sfccProduct.quantity ?? 1,
                            fulfillmentMethod: selectedDeliveryMethod,
                            type: TrackingParams.STANDARD_PDP,
                            autoshipFrequency:
                                sfccProduct.c_RecurringShipmentFrequency,
                            storeID: sfccProduct.c_fromStoreId,
                        },
                    },
                ],
                AddToCartType.STANDARD
            );
            trackAddToCart();
            trackAddToCartMethod(selectedDeliveryMethod);
        }

        if (currentStore) {
            const { searchTerm } = getSearchStats();
            const quantity = productData[0].quantity;
            velocityTrackAddToCart(
                selectedProduct,
                response.currency as Currency,
                NavigationListTypes.STANDARD_PDP,
                'no',
                [
                    mapAddToCartItems({
                        product,
                        selectedProduct,
                        quantity,
                        store: currentStore,
                        cartCount: getCartCount(response),
                        selectedDeliveryMethod,
                        lastSearchQuery: searchTerm,
                        frequency: sfccProduct?.c_RecurringShipmentFrequency,
                    }),
                ]
            );
        }

        return response;
    };

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        setCurrentVariantId(Number(selectedProduct.objectID));
    }, [selectedProduct.objectID, setCurrentVariantId]);

    useEffect(() => {
        const autoshipStatus = isAutoshipAvailable
            ? {
                  ats: webInventory.ats,
                  status: deliveryStockStatus as StockStatusEnum,
              }
            : {
                  ats: 0,
                  status: StockStatusEnum.OutOfStock,
              };

        trackEEProductDetailView(product, {
            // yeah they are basically the same, but (hopefully) not for long.
            // We are still mocking a lot of things here.
            [DeliveryType.STM]: {
                ats: webInventory.ats,
                status: deliveryStockStatus as StockStatusEnum,
            },
            [DeliveryType.AUTOSHIP]: autoshipStatus,
        });

        if (product.name) {
            trackProductView(product.name);
        }
    }, [product]);

    useEffect(() => {
        setCurrentBopisMinimumCaseQty(bopisMinimumCaseQty);
        setBopisStockStatus(
            validateStockType(
                {
                    stockQuantity: bopisInventory.ats,
                    minimumCaseQty: bopisMinimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setDeliveryMinimumCaseQty(minimumCaseQty);
        setDeliveryStockStatus(
            validateStockType(
                {
                    stockQuantity: webInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setSddStockStatus(
            validateStockType(
                {
                    stockQuantity: sddInventory.ats,
                    minimumCaseQty,
                    customPharmacyOOS,
                },
                inventoryLoading
            )
        );

        setIsInStoreOnly(
            typeof selectedProduct['shoppingOptions-isInStoreOnlyOverride'] !==
                'undefined'
                ? selectedProduct['shoppingOptions-isInStoreOnlyOverride']
                : Boolean(selectedProduct['shoppingOptions-isInStoreOnly'])
        );
    }, [
        product,
        selectedProduct,
        bopisInventory.ats,
        webInventory.ats,
        sddInventory.ats,
        setCurrentBopisMinimumCaseQty,
        bopisMinimumCaseQty,
        minimumCaseQty,
        customPharmacyOOS,
        inventoryLoading,
    ]);

    useEffect(() => {
        if (isEqual(selectedStore.store, currentStore)) {
            setCurrentStore(selectedStore.store);
        }
    }, [selectedStore, currentStore]);

    useEffect(() => {
        setSelectedDeliveryMethod(
            getDefaultSelectedInventory(
                isBopisDisabled,
                isSddDisabled,
                isAutoshipSupported,
                isShipToHomeSupported
            )
        );
    }, [
        selectedProduct,
        selectedVariants,
        isBopisDisabled,
        isSddDisabled,
        isAutoshipSupported,
        isShipToHomeSupported,
        setSelectedDeliveryMethod,
    ]);
    const [deliverySelection, setDeliverySelection] = useState<string>(
        `${
            radioSelection == 'autoship-opti'
                ? DeliveryType.AUTOSHIP
                : DeliveryType.BOPIS
        }`
    );

    const [selectedFrequency, setSelectedFrequency] = useState(
        AUTOSHIP_CONST.DEFAULT_OPTION
    );
    const frequencyItems = getFrequencyOptions(
        Boolean(orderGrooveData?.upcomingOrder)
    );

    const onFrequencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const frequency = event?.target.value;

        if (frequency) {
            setSelectedFrequency(frequency);
        }
    };
    // Optimizely START
    const host = window.location.hostname;
    const futureAuto = host.includes('.ca')
        ? 'Up to $30 & 5% on future orders'
        : 'Up to $20 & 5% on future orders';
    const { context: userContext } = useOptimizely();

    function fireOptiEvent(value: DeliveryType) {
        if ((value as string) === autoshipOpti) {
            userContext?.trackEvent('autoship___save_click');
        } else {
            userContext?.trackEvent('otp_click');
        }
    }
    // Optimizely END
    return (
        <div
            className="delivery-methods-opti"
            data-testid="mock-delivery-methods-opti"
        >
            {isAllMethodsDisabled ? (
                <InStoreOnly
                    stockStatus={bopisStockStatus as StockStatusEnum}
                />
            ) : (
                <>
                    {isAutoshipSupported ? (
                        <RadioField
                            data-testid={`delivery-type`}
                            key={radioSelection}
                            className={
                                !isClient
                                    ? 'delivery-methods-opti__parent disable-pointer-events'
                                    : 'delivery-methods-opti__parent '
                            }
                            onChange={(value: DeliveryType) => {
                                setRadioSelection(value);
                                fireOptiEvent(value); // Optimizely
                            }}
                            selectedValue={radioSelection}
                            defaultValue={oneTime}
                            variant="boxed"
                        >
                            <RadioField.Item
                                data-testid={`delivery-type__${oneTime}`}
                                className="one-time"
                                value={oneTime}
                                text={
                                    <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
                                        One-time purchase
                                    </span>
                                }
                                itemAfter={
                                    <TextPassage className="delivery-method-status"></TextPassage>
                                }
                            />
                            <RadioField.Item
                                data-testid={`delivery-type__${autoshipOpti}`}
                                className="autoship-opti"
                                value={autoshipOpti}
                                text={
                                    <>
                                        <div className="autoship-opti__parent">
                                            <AutoshipHeader
                                                productId={product.id.toString()}
                                                userHasSubscriptions={
                                                    !!orderGrooveData
                                                        ?.upcomingOrder
                                                        ?.displayShipmentDate
                                                }
                                                eligiblePromotions={
                                                    product?.eligiblePromotions
                                                }
                                            />
                                        </div>
                                        <AutoShipInfoDrawer
                                            autoship={autoship}
                                        />
                                    </>
                                }
                                itemAfter={
                                    <>
                                        {orderGrooveData?.upcomingOrder
                                            ?.displayShipmentDate ? (
                                            <></>
                                        ) : (
                                            <TextPassage>
                                                {futureAuto}
                                            </TextPassage>
                                        )}
                                        <TextPassage className="shipping-only">
                                            Only available via Shipping
                                        </TextPassage>
                                    </>
                                }
                                hiddenContent={
                                    <SelectField
                                        label="Frequency"
                                        data-testid="select-frequency"
                                        value={selectedFrequency}
                                        items={frequencyItems}
                                        onChange={onFrequencyChange}
                                    />
                                }
                            />
                        </RadioField>
                    ) : (
                        <></>
                    )}
                    {radioSelection === oneTime ? (
                        <RadioField
                            key={`${oneTime}-deliverySelection`}
                            variant="toggle"
                            className="delivery-options-children"
                            selectedValue={deliverySelection}
                            onChange={(value: DeliveryType) => {
                                setOneTimeRadioSelection(value);
                                setDeliverySelection(value);
                                setSelectedDeliveryMethod(value);
                            }}
                            defaultValue={DeliveryType.BOPIS}
                        >
                            {process.env.NEXT_PUBLIC_DISABLE_BOPIS !== 'true' &&
                                isBopisDisabled !== undefined && (
                                    <BopisTile
                                        groupName={oneTime}
                                        product={product}
                                        value={DeliveryType.BOPIS}
                                        isActive={
                                            selectedDeliveryMethod ===
                                            DeliveryType.BOPIS
                                        }
                                        isDisabled={isBopisDisabled}
                                        isPageLoading={isPageLoading}
                                        stockStatus={bopisStockStatus}
                                        selection={oneTimeRadioSelection}
                                        setSelection={setOneTimeRadioSelection}
                                        curbsideInfo={curbsideInfo}
                                        vetRxCats={vetRxCats}
                                        isDisabledByPersonalization={
                                            isPersonalized
                                        }
                                        minimumCaseQty={bopisMinimumCaseQty}
                                        inventoryQuantity={bopisInventory.ats}
                                    />
                                )}

                            {process.env.NEXT_PUBLIC_DISABLE_SDD !== 'true' && (
                                <SameDayDeliveryTile
                                    groupName={oneTime}
                                    value={DeliveryType.SDD}
                                    product={selectedProduct}
                                    isAvailable={!isSddDisabled}
                                    selection={oneTimeRadioSelection}
                                    setSelection={setOneTimeRadioSelection}
                                    isDisabled={isSddDisabled}
                                    isPageLoading={isPageLoading}
                                    isDisabledByPersonalization={isPersonalized}
                                    stockStatus={sddStockStatus}
                                    handleAddToCart={handleAddToCartWrapper}
                                    minimumCaseQty={deliveryMinimumCaseQty}
                                    inventoryQuantity={sddInventory.ats}
                                />
                            )}
                            {process.env.NEXT_PUBLIC_DISABLE_SHIPTOME !==
                                'true' && (
                                <ShipToMeTile
                                    groupName={oneTime}
                                    isAvailable={isShipToHomeSupported}
                                    isPageLoading={isPageLoading}
                                    value={DeliveryType.STM}
                                    selection={oneTimeRadioSelection}
                                    setSelection={setOneTimeRadioSelection}
                                    product={product}
                                    selectVariant={selectedProduct}
                                    handleAddToCart={handleAddToCartWrapper}
                                    inventory={webInventory}
                                    stockStatus={deliveryStockStatus}
                                    minimumCaseQty={deliveryMinimumCaseQty}
                                    inventoryQuantity={webInventory.ats}
                                />
                            )}
                        </RadioField>
                    ) : (
                        <></>
                    )}
                    {radioSelection === autoshipOpti ? (
                        <RadioField
                            key={`${autoshipOpti}-deliverySelection`}
                            className="delivery-options-children"
                            variant="toggle"
                            selectedValue={deliverySelection}
                            onChange={(value: DeliveryType) => {
                                setAutoshipRadioSelection(value);
                                setDeliverySelection(value);
                                setSelectedDeliveryMethod(value);
                            }}
                            defaultValue={DeliveryType.AUTOSHIP}
                        >
                            {process.env.NEXT_PUBLIC_DISABLE_BOPIS !== 'true' &&
                                isBopisDisabled !== undefined && (
                                    <BopisTile
                                        groupName={autoshipOpti}
                                        product={product}
                                        value={DeliveryType.BOPIS}
                                        isTileDisabled={true}
                                        isActive={false}
                                        isDisabled={true}
                                        isPageLoading={isPageLoading}
                                        stockStatus={bopisStockStatus}
                                        selection={autoshipRadioSelection}
                                        setSelection={setAutoshipRadioSelection}
                                        vetRxCats={vetRxCats}
                                        isDisabledByPersonalization={
                                            isPersonalized
                                        }
                                        minimumCaseQty={bopisMinimumCaseQty}
                                        inventoryQuantity={bopisInventory.ats}
                                        curbsideInfo={curbsideInfo}
                                    />
                                )}
                            {process.env.NEXT_PUBLIC_DISABLE_SDD !== 'true' && (
                                <SameDayDeliveryTile
                                    groupName={autoshipOpti}
                                    value={DeliveryType.SDD}
                                    product={selectedProduct}
                                    isAvailable={false}
                                    isTileDisabled={true}
                                    selection={autoshipRadioSelection}
                                    setSelection={setAutoshipRadioSelection}
                                    isDisabled={true}
                                    isPageLoading={isPageLoading}
                                    isDisabledByPersonalization={isPersonalized}
                                    stockStatus={sddStockStatus}
                                    handleAddToCart={handleAddToCartWrapper}
                                    minimumCaseQty={deliveryMinimumCaseQty}
                                    inventoryQuantity={sddInventory.ats}
                                />
                            )}
                            {process.env.NEXT_PUBLIC_DISABLE_AUTOSHIP !==
                                'true' && (
                                <AutoshipTile
                                    groupName={autoshipOpti}
                                    value={DeliveryType.AUTOSHIP}
                                    selection={autoshipRadioSelection}
                                    setSelection={setAutoshipRadioSelection}
                                    product={product}
                                    handleAddToCart={handleAddToCartWrapper}
                                    handleAddToExistingSubscription={
                                        handleAddToExistingSubscription
                                    }
                                    isActive={
                                        autoshipRadioSelection ===
                                        DeliveryType.AUTOSHIP
                                    }
                                    isAvailable={isAutoshipSupported}
                                    isPageLoading={isPageLoading}
                                    inventory={webInventory}
                                    inventoryQuantity={webInventory.ats}
                                    isDisabledByPersonalization={isPersonalized}
                                    minimumCaseQty={deliveryMinimumCaseQty}
                                    selectVariant={selectedProduct}
                                    selectedFrequency={selectedFrequency}
                                    stockStatus={deliveryStockStatus}
                                />
                            )}
                        </RadioField>
                    ) : (
                        // </>
                        <></>
                    )}
                    <AddedToCartDrawer
                        basketItems={basketItems}
                        onClose={() => setBasketItems([])}
                    />
                </>
            )}
        </div>
    );
}
